import { useState, useEffect } from 'react';
import { Calendar, CheckSquare, Clock, Filter, Plus, Search, Trash2, X, Check, AlertCircle, ChevronLeft, ChevronRight, Bell, UserCheck } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { Todo, todosRef, createDocument, getDocuments, updateDocument, deleteDocument, Influencer, influencersRef } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

const PRIORITIES = [
  { value: 'low', label: 'Basse', color: 'bg-blue-500/20 text-blue-300 border-blue-500/20' },
  { value: 'medium', label: 'Moyenne', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' },
  { value: 'high', label: 'Haute', color: 'bg-red-500/20 text-red-300 border-red-500/20' },
] as const;

const STATUSES = [
  { value: 'pending', label: 'En attente', color: 'bg-gray-500/20 text-gray-300 border-gray-500/20' },
  { value: 'in_progress', label: 'En cours', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' },
  { value: 'completed', label: 'Terminée', color: 'bg-green-500/20 text-green-300 border-green-500/20' },
] as const;

const WEEKDAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];

function TodoPage() {
  const { currentUser } = useAuth();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [agencyUsers, setAgencyUsers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Todo>>({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    dueDate: undefined,
    assignedTo: undefined,
  });
  const [customTitle, setCustomTitle] = useState('');
  const [isInfluencerTask, setIsInfluencerTask] = useState(false);
  const [influencerPrefix, setInfluencerPrefix] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch influencers
      const influencersData = await getDocuments<Influencer>(influencersRef);
      setInfluencers(influencersData.filter(inf => !inf.archived));
      
      // Get user's agency ID
      const userDoc = await getDoc(doc(db, 'users', currentUser!.uid));
      const userData = userDoc.data();
      const agencyId = userData?.agencyId;

      // Fetch all users from the same agency
      if (agencyId) {
        const usersQuery = query(collection(db, 'users'), where('agencyId', '==', agencyId));
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAgencyUsers(usersData);
      }
      
      // Fetch todos
      // Récupérer toutes les tâches de l'agence
      const todosQuery = query(collection(db, 'todos'), where('agencyId', '==', agencyId));
      const todosSnapshot = await getDocs(todosQuery);
      const todosData = todosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Todo[];
      
      // Filtrer les tâches :
      // - Garder les tâches personnelles (créées par ou assignées à l'utilisateur)
      // - Garder toutes les tâches d'influenceurs de l'agence
      const filteredTodos = todosData.filter(todo => 
        todo.title.startsWith('Tâche pour ') || // Toutes les tâches d'influenceurs
        todo.userId === currentUser?.uid || // Tâches créées par l'utilisateur
        todo.assignedTo === currentUser?.uid // Tâches assignées à l'utilisateur
      );
      
      // Fetch user names for all todos
      const userIds = [...new Set(todosData.map(todo => todo.userId))];
      const userNamesMap: Record<string, string> = {};
      
      await Promise.all(userIds.map(async (userId) => {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userNamesMap[userId] = userData.firstName || '';
        }
      }));
      
      setUserNames(userNamesMap);
      setTodos(filteredTodos);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Une erreur est survenue lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    const isInfluencerTask = formData.title?.startsWith('Tâche pour ');
    
    if (!currentUser) {
      setError('Utilisateur non connecté');
      return;
    }

    if (!formData.dueDate && !selectedDate) {
      setError('La date est requise');
      return;
    }

    // Get user's agencyId
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
    if (!userDoc.exists()) {
      setError('Profil utilisateur non trouvé');
      return;
    }
    const userData = userDoc.data();
    if (!userData.agencyId) {
      setError('Aucune agence associée à cet utilisateur');
      return;
    }

    const dueDate = formData.dueDate ? new Date(formData.dueDate) : new Date(selectedDate!);
    dueDate.setHours(12, 0, 0, 0);

    // Pour les tâches d'influenceurs, pas besoin d'assignation spécifique
    const assignedTo = isInfluencerTask ? null : (
      formData.assignedTo && formData.assignedTo.trim() !== '' 
        ? formData.assignedTo 
        : currentUser.uid
    );

    try {
      const todoData = {
        title: formData.title,
        description: formData.description || '',
        status: formData.status,
        priority: formData.priority,
        dueDate: dueDate.toISOString(),
        assignedTo,
        updatedAt: new Date(),
      };

      if (selectedTodo?.id) {
        await updateDocument(todosRef, selectedTodo.id, todoData);
      } else {
        const isAssigningToOther = formData.assignedTo && formData.assignedTo !== currentUser.uid;
        await createDocument(todosRef, {
          ...todoData,
          agencyId: userData.agencyId,
          userId: currentUser!.uid,
          status: 'pending',
          createdAt: new Date(),
        } as Todo);
        
        if (isAssigningToOther) {
          const assignedUser = agencyUsers.find(user => user.id === formData.assignedTo);
          if (assignedUser) {
            setSuccessMessage(`Tâche assignée à ${assignedUser.firstName} ${assignedUser.lastName}`);
          }
        }
      }
      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving todo:', error);
      setError('Une erreur est survenue lors de la sauvegarde');
    }
  };

  const handleStatusChange = async (todo: Todo, newStatus: Todo['status']) => {
    try {
      await updateDocument(todosRef, todo.id!, {
        status: newStatus,
        completedAt: newStatus === 'completed' ? new Date() : null,
        updatedAt: new Date(),
      });
      await loadData();
    } catch (error) {
      console.error('Error updating todo status:', error);
    }
  };

  const handleDelete = async (todoId: string) => {
    try {
      // Update local state by removing the deleted todo
      setTodos(prevTodos => prevTodos.filter(todo => todo.id !== todoId));
      
      // Delete the todo from Firestore
      try {
        await deleteDocument(todosRef, todoId);
        console.log(`Tâche ${todoId} supprimée avec succès`);
      } catch (deleteError) {
        console.error('Error in deleteDocument operation:', deleteError);
        // Restaurer la tâche dans l'état local si la suppression échoue
        loadData();
        setError('Erreur lors de la suppression de la tâche');
      }
    } catch (error) {
      console.error('Error deleting todo:', error);
      setError('Erreur lors de la suppression de la tâche');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTodo(null);
    setSelectedDate(null);
    setFormData({
      title: '',
      description: '',
      status: 'pending',
      priority: 'medium',
      assignedTo: undefined,
      dueDate: undefined,
    });
    setCustomTitle('');
    setIsInfluencerTask(false);
    setInfluencerPrefix('');
  };

  const handleAddTaskForDate = (date: Date) => {
    setSelectedDate(date);
    setFormData(prev => ({
      ...prev,
      dueDate: date.toISOString()
    }));
    setIsModalOpen(true);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getWeekDates = (date: Date) => {
    const monday = new Date(date);
    monday.setDate(monday.getDate() - monday.getDay() + 1);
    
    return WEEKDAYS.map((_, index) => {
      const day = new Date(monday);
      day.setDate(monday.getDate() + index);
      return day;
    });
  };

  const isValidDate = (date: any): date is Date => {
    return date instanceof Date && !isNaN(date.getTime());
  };

  const isSameDay = (date1: Date | string | undefined, date2: Date | string | undefined) => {
    if (!date1 || !date2) return false;
    
    try {
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      
      if (!isValidDate(d1) || !isValidDate(d2)) return false;
      
      return d1.getFullYear() === d2.getFullYear() &&
             d1.getMonth() === d2.getMonth() &&
             d1.getDate() === d2.getDate();
    } catch (error) {
      console.error('Error comparing dates:', error);
      return false;
    }
  };

  const getTodosForDate = (date: Date) => {
    return todos.filter(todo => {
      // Ne pas afficher les tâches d'influenceurs dans le calendrier
      if (!todo.dueDate || todo.title.startsWith('Tâche pour ')) return false;
      try {
        return isSameDay(new Date(todo.dueDate), date);
      } catch (error) {
        console.error('Invalid todo date:', todo.dueDate);
        return false;
      }
    });
  };

  const previousWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };

  const filteredTodos = todos.filter(todo => {
    const matchesSearch = 
      todo.title.toLowerCase().includes(search.toLowerCase()) ||
      (todo.description || '').toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || todo.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || todo.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const weekDates = getWeekDates(currentWeek);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <CheckSquare className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Tâches</h1>
            <p className="text-gray-400">Gérez vos tâches et suivez leur avancement</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
            <Bell className="w-5 h-5 flex-shrink-0" />
            <p>{successMessage}</p>
          </div>
        )}

        {/* Tâches hebdomadaires */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Semaine du {formatDate(weekDates[0])} au {formatDate(weekDates[4])}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={previousWeek}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextWeek}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-4">
              {weekDates.map((date, index) => (
                <div key={index} className="bg-gray-50 rounded-lg border border-gray-100">
                  <div className="p-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-gray-900 font-medium">{WEEKDAYS[index]}</h3>
                        <p className="text-sm text-gray-500">{formatDate(date)}</p>
                      </div>
                      <button
                        onClick={() => handleAddTaskForDate(date)}
                        className="p-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-indigo-600 transition-colors"
                        title="Ajouter une tâche"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="p-3 space-y-2 min-h-[200px]">
                    {getTodosForDate(date).map((todo) => (
                      <div 
                        key={todo.id} 
                        className="bg-white rounded-lg p-2 cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200"
                        onClick={() => {
                          setSelectedTodo(todo);
                          setFormData({
                            title: todo.title,
                            description: todo.description,
                            status: todo.status,
                            priority: todo.priority,
                            dueDate: todo.dueDate,
                          });
                          setIsModalOpen(true);
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(todo, todo.status === 'completed' ? 'pending' : 'completed');
                              }}
                              className={`p-1 rounded-lg transition-colors ${
                                todo.status === 'completed'
                                 ? 'bg-green-100 text-green-600'
                                 : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <span className="text-gray-900 text-sm truncate">{todo.title}</span>
                          </div>
                          <button
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleDelete(todo.id!);
                            }}
                            className="text-red-600 hover:text-red-700 flex-shrink-0 ml-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                            todo.priority === 'high' ? 'bg-red-100 text-red-600 border-red-200' :
                            todo.priority === 'medium' ? 'bg-yellow-100 text-yellow-600 border-yellow-200' :
                            'bg-blue-100 text-blue-600 border-blue-200'
                          }`}>
                            {PRIORITIES.find(p => p.value === todo.priority)?.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">
                            Par {userNames[todo.userId] || 'Utilisateur'}
                          </span>
                          {todo.assignedTo && (
                            <>
                              <span className="text-xs text-gray-500">•</span>
                              <span className="text-xs text-gray-500">
                                Assigné à {agencyUsers.find(u => u.id === todo.assignedTo)?.firstName || 'Utilisateur'}
                              </span>
                            </>
                          )}
                        </div>
                        {todo.description && (
                          <p className="text-sm text-gray-500 mt-2 line-clamp-2 break-words">
                            {todo.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

      </div>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-2xl w-full mx-4 p-6 border-2 border-black">
            <div className="absolute top-4 right-4">
              <button
                onClick={handleCloseModal}
                className="text-gray-600 hover:text-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {selectedTodo ? 'Modifier la tâche' : 'Nouvelle tâche'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Titre
                </label>
                {isInfluencerTask ? (
                  <div className="flex">
                    <div className="flex-shrink-0 bg-gray-100 text-gray-700 px-3 py-2 rounded-l-lg border-y-2 border-l-2 border-black">
                      {influencerPrefix}
                    </div>
                    <input
                      type="text"
                      value={customTitle}
                      onChange={(e) => {
                        setCustomTitle(e.target.value);
                        setFormData({ ...formData, title: `${influencerPrefix} – ${e.target.value}` });
                      }}
                      className="flex-1 bg-white border-y-2 border-r-2 border-black rounded-r-lg px-3 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                      placeholder="Description de la tâche"
                      required
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-white border-2 border-black rounded-lg px-3 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                    placeholder="Titre de la tâche"
                    required
                  />
                )}
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-white border-2 border-black rounded-lg px-3 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  placeholder="Description de la tâche"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                    Priorité
                  </label>
                  <select
                    id="priority"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as Todo['priority'] })}
                    className="w-full bg-white border-2 border-black rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  >
                    {PRIORITIES.map((priority) => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as Todo['status'] })}
                    className="w-full bg-white border-2 border-black rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  >
                    {STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Date d'échéance
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    value={formData.dueDate ? new Date(formData.dueDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full bg-white border-2 border-black rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                    Assigné à
                  </label>
                  <select
                    id="assignedTo"
                    value={formData.assignedTo || ''}
                    onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                    className="w-full bg-white border-2 border-black rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  >
                    <option value="">Moi</option>
                    {agencyUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {error && (
                <div className="bg-red-50 border-2 border-red-500 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-white border-2 border-black rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg border-2 border-black hover:bg-indigo-700 transition-colors"
                >
                  {selectedTodo ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default TodoPage;